// src/contexts/UserProfileContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import apiClient from '../common/apiClient';
import decryptResponse from '../common/decrypt';

const UserProfileContext = createContext(null);

export const PROPERTY_ACCESS_CITY_COOKIE = "propertyAccessCity";

// The list the City dropdowns used to hardcode. Only a fallback for when the
// cookie isn't set yet, so a required dropdown is never rendered empty.
export const ALL_CITIES = [
  "Ahmedabad West",
  "Ahmedabad East",
  "Gandhinagar",
  "Pune",
  "Mumbai",
];

// City pill rows (Owner Properties, Broker B2B, Filter, Residential Rental) render
// from PropertyAccessCity, so a user with ["Pune"] sees a Pune button and nothing
// else. This is only the fallback shown before the profile has loaded -- it keeps
// the row from rendering empty on first paint.
export const DEFAULT_PROPERTY_CITIES = [
  "Ahmedabad West",
  "Ahmedabad East",
  "Gandhinagar",
];

// Gujarati labels for the cities we have translations for. Any city that arrives
// from PropertyAccessCity without an entry here falls back to its English name.
export const CITY_LABELS_GU = {
  "Ahmedabad West": "અમદાવાદ પશ્ચિમ",
  "Ahmedabad East": "અમદાવાદ પૂર્વ",
  "Gandhinagar": "ગાંધીનગર",
  "Pune": "પુણે",
  "Mumbai": "મુંબઈ",
};

const normalizeCities = (list) =>
  Array.isArray(list)
    ? list.map((c) => (typeof c === "string" ? c.trim() : "")).filter(Boolean)
    : [];

// Extract the list of cities the user is allowed to access, strictly from
// PropertyAccessCity in the profile response (checked at every level it can appear).
// If PropertyAccessCity has no city names, the result is empty and NO city is clickable.
const getPropertyAccessCities = (profile) => {
  if (!profile) return [];
  return normalizeCities(
    [
      profile.PropertyAccessCity,
      profile.user?.PropertyAccessCity,
      profile.data?.PropertyAccessCity,
      profile.data?.user?.PropertyAccessCity,
    ].find((c) => Array.isArray(c) && c.length > 0)
  );
};

// Cache the list in a cookie so the property / requirement forms can read it
// synchronously, before the profile fetch resolves. Called from the login
// response too. No-op when the payload has no PropertyAccessCity, so a response
// missing the field never wipes a good cookie. Expiry matches token/userId.
export const savePropertyAccessCities = (payload) => {
  const cities = getPropertyAccessCities(payload);
  if (!cities.length) return [];
  Cookies.set(PROPERTY_ACCESS_CITY_COOKIE, JSON.stringify(cities), { expires: 365 });
  return cities;
};

const readPropertyAccessCityCookie = () => {
  const raw = Cookies.get(PROPERTY_ACCESS_CITY_COOKIE);
  if (!raw) return [];
  try {
    return normalizeCities(JSON.parse(raw));
  } catch {
    // Tolerate a legacy/manually-set comma separated value.
    return normalizeCities(raw.split(","));
  }
};

// Options for a City dropdown: the user's accessible cities, or the full list
// when we don't know them yet (cookie not written, e.g. a pre-existing session).
export const getCityOptions = () => {
  const cities = readPropertyAccessCityCookie();
  return cities.length ? cities : ALL_CITIES;
};

// Keeps an already-saved city selectable while editing, even if the user's
// access has since changed - otherwise the <select> would silently blank it.
export const withSelectedCity = (options, selected) => {
  const city = typeof selected === "string" ? selected.trim() : "";
  if (!city) return options;
  const exists = options.some((c) => c.toLowerCase() === city.toLowerCase());
  return exists ? options : [...options, city];
};

export const UserProfileProvider = ({ children }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [lastFetched, setLastFetched] = useState(0);
  const userId = Cookies.get("userId");

  const fetchUserProfile = useCallback(async (currentUserId, force = false) => {
    if (!currentUserId) {
      setProfileError("User ID is missing.");
      setLoadingProfile(false);
      return null;
    }

    // Cache: If fetched in the last 30 seconds and not forced, return existing profile
    const now = Date.now();
    if (!force && userProfile && (now - lastFetched < 30000)) {
      setLoadingProfile(false);
      return userProfile;
    }

    setLoadingProfile(true);
    setProfileError(null);
    try {
      const token = Cookies.get("token");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await apiClient(
        "post",
        `/cjidnvij/ceksfbuebijn/user/dsvsdv/v2/casadyt/${currentUserId}/csauyv`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      let finalData = response;
      if (response?.error?.encrypted) {
        const decrypted = decryptResponse(response.error.encrypted);
        if (decrypted) {
          finalData = decrypted;
        } else {
          throw new Error("Failed to decrypt profile response.");
        }
      }

      if (finalData.success && finalData.data) {
        const profileData = finalData.data.data || finalData.data;

        const resolvedIsPremium =
          profileData?.user?.isPremium ??
          profileData?.isPremium ??
          (profileData?.adminExpiryInfo?.expiryDate ? 1 : 0);
        Cookies.set("isPremium", String(resolvedIsPremium), { expires: 365 });

        // Cache the access cities so forms can read them without the profile.
        savePropertyAccessCities(profileData);

        setUserProfile(profileData);
        setLastFetched(Date.now());
        return profileData;
      } else {
        throw new Error(finalData.message || "Failed to fetch user profile.");
      }
    } catch (err) {
      console.error("Error fetching user profile:", err);
      setProfileError(err.message || "An unexpected error occurred while fetching profile.");
      setUserProfile(null);
      return null;
    } finally {
      setLoadingProfile(false);
    }
  }, [userProfile, lastFetched]);

  useEffect(() => {
    if (userId) {
      fetchUserProfile(userId);
    } else {
      setLoadingProfile(false);
      setUserProfile(null);
      setLastFetched(0);
    }
  }, [userId, fetchUserProfile]);

  return (
    <UserProfileContext.Provider value={{
      userProfile,
      loadingProfile,
      profileError,
      fetchUserProfile,
      // Cities the user is allowed to access (from PropertyAccessCity)
      propertyAccessCities: getPropertyAccessCities(userProfile),
      // A city is clickable ONLY if it is listed in PropertyAccessCity.
      // If PropertyAccessCity is empty/missing, no city is clickable.
      hasCityAccess: (cityName) => {
        const accessCities = getPropertyAccessCities(userProfile);
        const normalized = String(cityName || "").trim().toLowerCase();
        return accessCities.some(
          (c) => c.toLowerCase() === normalized
        );
      },
      hasFeature: (featureKey) => {
        if (!userProfile || !featureKey) return false;

        const normalizeFeature = (value) =>
          typeof value === "string" ? value.trim().toUpperCase() : "";
        const isActiveStatus = (value) =>
          (value || "").toString().trim().toLowerCase() === "active";

        const normalizedFeatureKey = normalizeFeature(featureKey);
        if (!normalizedFeatureKey) return false;

        const activeFeatures = new Set();

        // 1. Read direct FeaturesAccessData: [{ FeaturesName, status }]
        if (Array.isArray(userProfile.FeaturesAccessData)) {
          userProfile.FeaturesAccessData.forEach((item) => {
            if (!isActiveStatus(item?.status)) return;
            const normalized = normalizeFeature(item?.FeaturesName);
            if (normalized) activeFeatures.add(normalized);
          });
        }

        // Some API responses nest the same array under `user`.
        if (Array.isArray(userProfile.user?.FeaturesAccessData)) {
          userProfile.user.FeaturesAccessData.forEach((item) => {
            if (!isActiveStatus(item?.status)) return;
            const normalized = normalizeFeature(item?.FeaturesName);
            if (normalized) activeFeatures.add(normalized);
          });
        }

        // 2. Read payment history feature objects/features arrays
        if (Array.isArray(userProfile.paymentHistory)) {
          userProfile.paymentHistory.forEach((payment) => {
            if (Array.isArray(payment?.FeaturesAccessData)) {
              payment.FeaturesAccessData.forEach((item) => {
                if (!isActiveStatus(item?.status)) return;
                const normalized = normalizeFeature(item?.FeaturesName);
                if (normalized) activeFeatures.add(normalized);
              });
            }

            if (isActiveStatus(payment?.status) && Array.isArray(payment?.features)) {
              payment.features.forEach((feature) => {
                const normalized = normalizeFeature(feature);
                if (normalized) activeFeatures.add(normalized);
              });
            }
          });
        }

        // 3. Special logic: If user has BROKER_PROPERTY or AGENT_REQUIREMENT, they get both
        const BROKER_KEY = "BROKER_PROPERTY";
        const AGENT_KEY = "AGENT_REQUIREMENT";

        if (activeFeatures.has(BROKER_KEY) || activeFeatures.has(AGENT_KEY)) {
          activeFeatures.add(BROKER_KEY);
          activeFeatures.add(AGENT_KEY);
        }

        return activeFeatures.has(normalizedFeatureKey);
      }
    }}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};
